import React, { FunctionComponent, ReactNode } from 'react'
import { useWindowDimensions } from 'react-native'

import styled from 'styled-components/native'

import { IS_WEB } from '../../constants'
import {
  CLIENT_PROFILE_HEADER_BOTTOM_CSS_VAR,
  PAGE_SCROLLBAR_WIDTH_CSS_VAR,
} from '../../hooks/useClientProfileHeaderCollapsed'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const SIDE_PANEL_BREAKPOINT = 1280
const SIDE_PANEL_WIDTH = 320
const SIDE_PANEL_WIDE_WIDTH = 375

export const useSidePanelWidth = (): number => {
  const { width } = useWindowDimensions()
  return width >= SIDE_PANEL_BREAKPOINT ? SIDE_PANEL_WIDE_WIDTH : SIDE_PANEL_WIDTH
}

const Container = styled.View<{ theme: ThemeType; panelWidth: number }>(({ theme: { colors }, panelWidth }) => ({
  width: panelWidth,
  backgroundColor: colors.backgroundPrimary,
  borderLeftWidth: 1,
  borderLeftColor: colors.borderDefault,
  borderStyle: 'solid',
  ...(IS_WEB && {
    position: 'fixed',
    top: `var(${CLIENT_PROFILE_HEADER_BOTTOM_CSS_VAR}, 0px)`,
    bottom: 0,
    right: `var(${PAGE_SCROLLBAR_WIDTH_CSS_VAR}, 0px)`,
    overflowY: 'auto',
  }),
}))

const SectionDivider = styled.View(({ theme }) => ({
  width: '100%',
  height: 1,
  backgroundColor: theme.colors.borderDefault,
}))

export type ClientDetailsSidePanelProps = {
  sponsoredBenefits?: ReactNode
  medications?: ReactNode
  aiConversations?: ReactNode
  careServices?: ReactNode
  contacts?: ReactNode
}

export const ClientDetailsSidePanel: FunctionComponent<ClientDetailsSidePanelProps> = ({
  sponsoredBenefits,
  medications,
  aiConversations,
  careServices,
  contacts,
}) => {
  const panelWidth = useSidePanelWidth()
  const sections = [
    { id: 'sponsored-benefits', content: sponsoredBenefits },
    { id: 'medications', content: medications },
    { id: 'ai-conversations', content: aiConversations },
    { id: 'care-services', content: careServices },
    { id: 'contacts', content: contacts },
  ].filter(({ content }) => content !== undefined && content !== null && content !== false)

  return (
    <Container panelWidth={panelWidth} testID={tID('ClientDetails-sidepanel')}>
      {sections.map(({ id, content }, index) => (
        <React.Fragment key={id}>
          {index > 0 && <SectionDivider />}
          {content}
        </React.Fragment>
      ))}
    </Container>
  )
}
